import React, { useState, useEffect, useRef, useCallback } from 'react';
import { formatUntil } from '../utils/formatUntil';
import { useAuthenticator } from '@aws-amplify/ui-react';
import { fetchUserAttributes, updateUserAttributes, fetchAuthSession, updatePassword } from 'aws-amplify/auth';
import {
  User, CreditCard, Shield, Database, LifeBuoy, Trash2,
  Pencil, Camera, Download, RefreshCw, CheckCircle2, AlertCircle,
  ChevronDown, X, Info, Receipt, Bell,
} from 'lucide-react';
import { apiGet, apiPost, apiPut, apiDel } from '../api';
import { parseApiError } from '../apiError';
import VerifyEmailInline from '../components/VerifyEmailInline';
import { formatMoney, planPrice, recordedCurrency } from '../pricing/prices';
import { useNavigate } from 'react-router-dom';
import RegenerateRecoveryKey from '../components/RegenerateRecoveryKey';

// ── Types ─────────────────────────────────────────────────────────────────────

interface Profile {
  bio: string;
  state: string;
  country: string;
  avatarKey: string | null;
  avatarUrl: string | null;
  // Product-update announcements only. Absent from older server builds, so it
  // defaults to subscribed rather than rendering the toggle off by mistake.
  emailUpdates: boolean;
}

interface Subscription {
  active: boolean;
  status: 'active' | 'cancelled' | 'none' | 'refunded';
  expiry: number;
  /** End of the period actually billed. May be earlier than `expiry`. (#706) */
  paidPeriodEnd?: number;
  plan: 'monthly' | 'annual' | 'admin' | 'desktop' | 'trial' | 'collaborator' | 'none';
  permanent: boolean;
  purchaseDate?: number;
  isTrial?: boolean;
  trialDaysRemaining?: number | null;
  /** Set to cancel at the end of the period: still entitled, will not renew. */
  cancelAtPeriodEnd?: boolean;
  bookLimit?: number | null;
  /** The currency Stripe bills this subscription in, as the webhook recorded it. */
  currency?: string;
  /** Where the paid period began, and which plan paid for it. (#832) */
  paidPeriodStart?: number | null;
  paidPlan?: 'monthly' | 'annual' | null;
  /** A switch to monthly scheduled for the end of the paid year. (#832) */
  pendingPlan?: 'monthly' | 'annual' | null;
  pendingPlanAt?: number | null;
}

/**
 * What a plan switch would do, as the server priced it from Stripe at one moment.
 * Sent back unchanged to confirm, so what is charged is what was shown. (#832)
 */
interface SwitchPreview {
  to: 'annual' | 'monthly';
  trialing: boolean;
  /** Annual to monthly outside a trial: waits for the end of the paid year. */
  scheduled: boolean;
  /** Charged today, in minor units of `currency`. */
  chargeNow: number;
  currency?: string;
  /** When the new price is first charged, or when a scheduled switch happens. */
  at?: number;
  yearEndsAt?: number | null;
  prorationDate?: number;
}

// ── Constants ─────────────────────────────────────────────────────────────────

// monthly and annual come from src/pricing/prices.ts in the currency the subscriber is
// actually billed in, so a Canadian paying CAD is not shown "$9.99". (#826)
const PLAN_PRICE: Record<string, string> = {
  admin:   'Administrator',
  desktop: 'Desktop License (one-time)',
  trial:   'Free Trial',
  none:    '—',
};

const NAV_ITEMS = [
  { id: 'profile',  label: 'Profile',        icon: User,       danger: false },
  { id: 'plan',     label: 'Plan & Billing',  icon: CreditCard, danger: false },
  { id: 'security', label: 'Security',        icon: Shield,     danger: false },
  { id: 'data',     label: 'Your Data',       icon: Database,   danger: false },
  { id: 'emails',   label: 'Email Preferences', icon: Bell,     danger: false },
  { id: 'support',  label: 'Support',         icon: LifeBuoy,   danger: false },
  { id: 'danger',   label: 'Delete Account',  icon: Trash2,     danger: true  },
] as const;

// ── Helpers ───────────────────────────────────────────────────────────────────

// `expiry` is when access ends; `paidPeriodEnd` is the end of the period that was
// billed. A trial carry-over (#706) makes the first later than the second, and the
// bar measures progress through the PAID period — deriving its start from expiry
// instead pinned it at 0% for the whole carried stretch while the label counted
// down from 58 days under a heading that said "Monthly Plan".
//
// Where the webhook recorded the paid period's start, the bar runs from that to its end
// rather than assuming 30 or 365 days: a switch to annual bills a year that began with
// the monthly subscription, from the day of the switch. (#832)
function getTimeRemaining(expiry: number, plan: string, paidPeriodEnd?: number, paidPeriodStart?: number | null) {
  const now = Date.now();
  const msLeft = Math.max(0, expiry - now);
  const daysLeft = Math.ceil(msLeft / (1000 * 60 * 60 * 24));
  const end = paidPeriodEnd || expiry;
  const periodMs = paidPeriodStart && paidPeriodStart < end
    ? end - paidPeriodStart
    : (plan === 'annual' ? 365 : 30) * 24 * 60 * 60 * 1000;
  const periodStart = end - periodMs;
  const elapsed = now - periodStart;
  const progress = Math.min(100, Math.max(0, (elapsed / periodMs) * 100));
  return { daysLeft, progress };
}

const longDate = (ms: number) => new Date(ms).toLocaleDateString('en-US', { year: 'numeric', month: 'long', day: 'numeric' });

function getInitials(name: string): string {
  const parts = name.trim().split(/\s+/);
  if (parts.length === 1) return parts[0][0]?.toUpperCase() ?? '?';
  return (parts[0][0] + parts[parts.length - 1][0]).toUpperCase();
}

// ── Component ─────────────────────────────────────────────────────────────────

/** `emailVerified` comes from App, which reads the ID token's claim; verifying here tells
 *  App too. Both required, so the build checks App's wiring. */
const AccountPage: React.FC<{ emailVerified: boolean; onEmailVerified: () => void }> = ({ emailVerified, onEmailVerified }) => {
  const { user, signOut } = useAuthenticator((context) => [context.user]);
  const navigate = useNavigate();
  const fileInputRef = useRef<HTMLInputElement>(null);
  // The read-only note, focused when a refused edit locks the card (#1051).
  const lockNoteRef = useRef<HTMLDivElement>(null);
  const focusLockNote = useRef(false);
  // Focus targets for verification (#1052, #1130): the Profile heading once verified (the
  // form that had focus is gone), and the verify section when the server refuses the switch.
  const profileHeadingRef = useRef<HTMLHeadingElement>(null);
  const verifyRef = useRef<HTMLDivElement>(null);
  const focusVerify = useRef(false);
  const [serverSaysUnverified, setServerSaysUnverified] = useState(false);

  // ── Core state ─────────────────────────────────────────────────────────────
  const [subscription, setSubscription] = useState<Subscription | null>(null);
  const [loading, setLoading] = useState(true);
  const [feedback, setFeedback] = useState<{ message: string; type: 'success' | 'error' } | null>(null);

  // Cognito display name
  const [displayName, setDisplayName] = useState('');
  const [editName, setEditName] = useState('');
  const [editingName, setEditingName] = useState(false);
  const [savingName, setSavingName] = useState(false);

  // Author profile
  const [profile, setProfile] = useState<Profile>({ bio: '', state: '', country: '', avatarKey: null, avatarUrl: null, emailUpdates: true });
  // emailUpdates is deliberately outside editProfile: it saves the moment it is
  // flipped, rather than waiting for the profile form's Save button.
  const [editProfile, setEditProfile] = useState<Omit<Profile, 'avatarUrl' | 'emailUpdates'>>({ bio: '', state: '', country: '', avatarKey: null });
  const [savingEmailPref, setSavingEmailPref] = useState(false);
  const [emailPrefError, setEmailPrefError] = useState<string | null>(null);
  const [savingProfile, setSavingProfile] = useState(false);
  const [uploadingAvatar, setUploadingAvatar] = useState(false);
  const [avatarPreview, setAvatarPreview] = useState<string | null>(null);

  // Subscription modals
  const [showCancelModal, setShowCancelModal] = useState(false);
  const [cancelReason, setCancelReason] = useState('');
  const [cancelling, setCancelling] = useState(false);
  const [showRefundModal, setShowRefundModal] = useState(false);
  const [refunding, setRefunding] = useState(false);
  const [refundReason, setRefundReason] = useState('');
  // Plan switch (#832)
  const [switchTo, setSwitchTo] = useState<'annual' | 'monthly' | null>(null);
  const [switchPreview, setSwitchPreview] = useState<SwitchPreview | null>(null);
  const [switchError, setSwitchError] = useState<string | null>(null);
  const switchReturnFocus = useRef<HTMLElement | null>(null);
  const switchFirstButton = useRef<HTMLButtonElement | null>(null);
  const [switching, setSwitching] = useState(false);
  const [keepingAnnual, setKeepingAnnual] = useState(false);

  // Support accordion
  const [supportOpen, setSupportOpen] = useState<string | null>(null);
  const toggleSupport = (id: string) => setSupportOpen(prev => prev === id ? null : id);

  // Account
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [exportingData, setExportingData] = useState(false);
  const [managingBilling, setManagingBilling] = useState(false);

  // Security
  // What the server said of the recovery key: 'loading' until it answers, 'none' only
  // when it said there is none, 'set' when it said there is one (with its date), and
  // 'unknown' when the first read failed. Create is offered on 'none' alone: on a failed
  // read the account may have a key, and the flow would replace it with no warning (#1105).
  const [recoveryKeyStatus, setRecoveryKeyStatus] = useState<'loading' | 'none' | 'set' | 'unknown'>('loading');
  const [recoveryKeyDate, setRecoveryKeyDate] = useState<string | null>(null);
  // The email claim on the ID token: where the server sends the code for a new key.
  const [tokenEmail, setTokenEmail] = useState('');

  // Security — change password
  const [showPasswordModal, setShowPasswordModal] = useState(false);
  const [currentPassword, setCurrentPassword] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [changingPassword, setChangingPassword] = useState(false);
  const [passwordError, setPasswordError] = useState<string | null>(null);
  const [isFederated, setIsFederated] = useState(false);

  // Admin
  const [isHelpAdmin, setIsHelpAdmin] = useState(false);

  // Scrollspy
  const [activeSection, setActiveSection] = useState<string>('profile');

  // ── Flash helper ───────────────────────────────────────────────────────────
  const flash = useCallback((message: string, type: 'success' | 'error' = 'success') => {
    setFeedback({ message, type });
  }, []);

  // ── Data fetching ──────────────────────────────────────────────────────────
  const fetchSubscriptionStatus = async (): Promise<Subscription | null> => {
    try {
      const res = await apiGet('/subscriptions/status');
      const fresh = await res.body.json() as unknown as Subscription;
      setSubscription(fresh);
      return fresh;
    } catch (err) {
      console.error('[AccountPage] subscription fetch error:', err);
      return null;
    }
  };

  const loadProfile = async () => {
    try {
      const res = await apiGet('/profile');
      const raw = await res.body.json() as unknown as Partial<Profile>;
      const data: Profile = {
        bio:       raw.bio       ?? '',
        state:     raw.state     ?? '',
        country:   raw.country   ?? '',
        avatarKey: raw.avatarKey ?? null,
        avatarUrl: raw.avatarUrl ?? null,
        emailUpdates: raw.emailUpdates ?? true,
      };
      setProfile(data);
      setEditProfile({ bio: data.bio, state: data.state, country: data.country, avatarKey: data.avatarKey });
    } catch { /* no profile yet */ }
  };

  // Saves immediately on flip. Optimistic so the switch feels instant, and reverted
  // on failure so the control never claims a preference the server did not accept —
  // getting this wrong would silently keep emailing someone who opted out.
  const setEmailUpdates = async (next: boolean) => {
    const previous = profile.emailUpdates;
    setProfile(p => ({ ...p, emailUpdates: next }));
    setSavingEmailPref(true);
    setEmailPrefError(null);
    try {
      await apiPut('/profile', { emailUpdates: next });
      flash(next ? 'You will get product update emails.' : 'You will no longer get product update emails.');
    } catch (err) {
      setProfile(p => ({ ...p, emailUpdates: previous }));
      const { code, message } = await parseApiError(err);
      // The page thought the address verified and the server did not (a token that
      // could not refresh, say). Offer the verification rather than a dead end (#1052).
      if (code === 'EMAIL_NOT_VERIFIED') {
        focusVerify.current = true;
        setServerSaysUnverified(true);
        return;
      }
      setEmailPrefError(message || 'Could not save that. Try again.');
    } finally {
      setSavingEmailPref(false);
    }
  };

  const fetchRecoveryKeyStatus = async () => {
    try {
      const res = await apiGet('/account/recovery-key-status');
      const data = await res.body.json() as { exists?: boolean; createdAt?: string };
      if (data.exists === true) {
        setRecoveryKeyDate(data.createdAt
          ? new Date(data.createdAt).toLocaleDateString('en-US', { year: 'numeric', month: 'long', day: 'numeric' })
          : null);
        setRecoveryKeyStatus('set');
      } else if (data.exists === false) {
        setRecoveryKeyStatus('none');
      } else {
        setRecoveryKeyStatus(s => (s === 'loading' ? 'unknown' : s));
      }
    } catch {
      // Only the first read can leave the status unknown. A re-read (after a key may have
      // changed, with the flow still open) that fails keeps the last answer, and its date,
      // so the open flow is not taken away with the row's button. Keys are never deleted,
      // so a kept 'set' stays true; a stale 'none' is caught by the code request's hasKey,
      // which turns the flow to the replace words before any code is entered.
      setRecoveryKeyStatus(s => (s === 'loading' ? 'unknown' : s));
    }
  };

  useEffect(() => {
    Promise.all([
      fetchSubscriptionStatus(),
      loadProfile(),
      fetchRecoveryKeyStatus(),
      fetchUserAttributes().then(attrs => {
        const name = attrs.name ?? '';
        setDisplayName(name);
        setEditName(name);
      }).catch(() => {}),
      fetchAuthSession().then(session => {
        const payload = session.tokens?.idToken?.payload;
        const groups = (payload?.['cognito:groups'] as string[] | undefined) ?? [];
        setIsHelpAdmin(groups.includes('Admins'));
        // Federated (Google/Apple) users have an `identities` claim and no Cognito password.
        const identities = payload?.identities as unknown[] | undefined;
        setIsFederated(Array.isArray(identities) && identities.length > 0);
        setTokenEmail(typeof payload?.email === 'string' ? payload.email : '');
      }).catch(() => {}),
    ]).finally(() => setLoading(false));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // ── Scrollspy ──────────────────────────────────────────────────────────────
  useEffect(() => {
    const handleScroll = () => {
      const sections = NAV_ITEMS.map(item => ({
        id: item.id,
        el: document.getElementById(`acct-${item.id}`),
      }));
      let active = 'profile';
      for (const { id, el } of sections) {
        if (el) {
          const top = el.getBoundingClientRect().top;
          if (top <= 120) active = id;
        }
      }
      setActiveSection(active);
    };
    window.addEventListener('scroll', handleScroll, { passive: true });
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  // A plan that ended while the page was open: the server refused the edit. Re-read the
  // plan so the Profile card turns read-only and says why, rather than "try again",
  // which cannot help (#1051). True only when the re-read confirms it; otherwise the
  // caller says the action failed, as it always did.
  const planEnded = async (err: unknown): Promise<boolean> => {
    if ((await parseApiError(err)).code !== 'SUBSCRIPTION_REQUIRED') return false;
    focusLockNote.current = true;
    const fresh = await fetchSubscriptionStatus();
    if (fresh?.active === false) return true;
    focusLockNote.current = false;
    return false;
  };

  // ── Avatar ─────────────────────────────────────────────────────────────────
  const handleAvatarClick = () => fileInputRef.current?.click();

  const handleAvatarChange = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (file.size > 5 * 1024 * 1024) {
      flash('Photo must be under 5MB.', 'error');
      return;
    }
    setUploadingAvatar(true);
    setAvatarPreview(URL.createObjectURL(file));
    try {
      const res = await apiPost('/profile/avatar', { contentType: file.type });
      const { uploadUrl, avatarKey } = await res.body.json() as { uploadUrl: string; avatarKey: string };
      await fetch(uploadUrl, { method: 'PUT', body: file, headers: { 'Content-Type': file.type } });
      setEditProfile(p => ({ ...p, avatarKey }));
      flash('Photo uploaded — save your profile to apply it.');
    } catch (err) {
      setAvatarPreview(null);
      if (await planEnded(err)) return;
      flash('Photo upload failed. Please try again.', 'error');
    } finally {
      setUploadingAvatar(false);
      if (fileInputRef.current) fileInputRef.current.value = '';
    }
  };

  // ── Profile ────────────────────────────────────────────────────────────────
  const handleSaveProfile = async () => {
    setSavingProfile(true);
    try {
      await apiPut('/profile', editProfile);
      await loadProfile();
      setAvatarPreview(null);
      flash('Profile saved.');
    } catch (err) {
      if (await planEnded(err)) return;
      flash('Failed to save profile. Please try again.', 'error');
    } finally {
      setSavingProfile(false);
    }
  };

  const handleSaveName = async () => {
    if (!editName.trim()) return;
    setSavingName(true);
    try {
      await updateUserAttributes({ userAttributes: { name: editName.trim() } });
      setDisplayName(editName.trim());
      setEditingName(false);
      flash('Display name updated.');
    } catch {
      flash('Failed to update display name. Please try again.', 'error');
    } finally {
      setSavingName(false);
    }
  };

  // ── Billing portal ─────────────────────────────────────────────────────────
  const handleManageBilling = async () => {
    setManagingBilling(true);
    try {
      const res = await apiPost('/stripe/portal');
      const { url } = await res.body.json() as { url: string };
      window.location.href = url;
    } catch {
      flash('Failed to open billing portal. Please try again.', 'error');
    } finally {
      setManagingBilling(false);
    }
  };

  // ── Change password ──────────────────────────────────────────────────────────
  // Matches the Cognito pool policy: >=8 chars, lower, upper, digit.
  const passwordPolicyOk = (pw: string) =>
    pw.length >= 8 && /[a-z]/.test(pw) && /[A-Z]/.test(pw) && /[0-9]/.test(pw);

  const handleChangePassword = async () => {
    setPasswordError(null);
    if (!currentPassword || !newPassword) { setPasswordError('All fields are required.'); return; }
    if (!passwordPolicyOk(newPassword)) {
      setPasswordError('New password must be at least 8 characters and include an uppercase letter, a lowercase letter, and a number.');
      return;
    }
    if (newPassword !== confirmPassword) { setPasswordError('New passwords do not match.'); return; }
    if (newPassword === currentPassword) { setPasswordError('New password must be different from your current password.'); return; }
    setChangingPassword(true);
    try {
      await updatePassword({ oldPassword: currentPassword, newPassword });
      setShowPasswordModal(false);
      setCurrentPassword(''); setNewPassword(''); setConfirmPassword('');
      flash('Password updated.');
    } catch (e: unknown) {
      const name = (e as { name?: string })?.name;
      if (name === 'NotAuthorizedException') setPasswordError('Current password is incorrect.');
      else if (name === 'InvalidPasswordException') setPasswordError('New password does not meet the requirements.');
      else if (name === 'LimitExceededException') setPasswordError('Too many attempts. Please try again later.');
      else setPasswordError('Could not change password. Please try again.');
    } finally {
      setChangingPassword(false);
    }
  };

  const closePasswordModal = () => {
    setShowPasswordModal(false);
    setCurrentPassword(''); setNewPassword(''); setConfirmPassword(''); setPasswordError(null);
  };

  // ── Subscription ───────────────────────────────────────────────────────────
  const handleCancelSubscription = async () => {
    setCancelling(true);
    try {
      await apiPost('/subscriptions/cancel', { reason: cancelReason });
      setShowCancelModal(false);
      setCancelReason('');
      flash('Subscription cancelled. You retain full access until your billing period ends.');
      fetchSubscriptionStatus();
    } catch {
      flash('Failed to cancel. Please contact support@trychaptery.com.', 'error');
    } finally {
      setCancelling(false);
    }
  };

  const handleRefundRequest = async () => {
    setRefunding(true);
    try {
      const res = await apiPost('/subscriptions/refund-request', { reason: refundReason });
      const data = await res.body.json() as { success?: boolean; eligible?: boolean; reason?: string };
      if (data.eligible === false) {
        const msg = data.reason === 'outside_window'
          ? 'You are outside the refund window for your plan.'
          : data.reason === 'already_refunded'
            ? 'This subscription has already been refunded.'
            : data.reason === 'annual_not_started'
              ? 'Your annual plan has not started yet, so there is no annual payment to refund.'
              : 'Your plan is not eligible for a refund.';
        flash(msg, 'error');
      } else {
        setShowRefundModal(false);
        const refunded = (data as { refunded?: number; currency?: string }).refunded;
        const refundedIn = recordedCurrency((data as { currency?: string }).currency);
        flash(refunded
          ? `Refunded ${formatMoney(refunded / 100, refundedIn)}. It will appear on your statement within 5–10 business days.`
          : 'Refund processed. It will appear on your statement within 5–10 business days.');
        fetchSubscriptionStatus();
      }
    } catch {
      flash('Refund request failed. Please contact support@trychaptery.com.', 'error');
    } finally {
      setRefunding(false);
    }
  };

  // ── Plan switch (#832) ─────────────────────────────────────────────────────
  // The price is asked of Stripe before anything is charged, and shown exactly.
  const openSwitch = async (to: 'annual' | 'monthly') => {
    switchReturnFocus.current = document.activeElement as HTMLElement | null;
    setSwitchTo(to);
    setSwitchPreview(null);
    setSwitchError(null);
    try {
      const res = await apiPost('/subscriptions/switch', { action: 'preview', to });
      setSwitchPreview(await res.body.json() as unknown as SwitchPreview);
    } catch (err) {
      setSwitchError((await parseApiError(err)).message || 'Could not work out the price. Try again.');
    }
  };

  // The webhook records a switch to annual a second or two after Stripe charges, so
  // the page waits for it rather than showing the old plan as if nothing happened.
  // Shown once it has settled, or on the last try: a half-updated status (the plan
  // recorded, the paid year not yet) would flash a card that contradicts itself.
  const refreshUntil = async (done: (s: Subscription) => boolean) => {
    const tries = 8;
    for (let i = 0; i < tries; i += 1) {
      try {
        const res = await apiGet('/subscriptions/status');
        const next = await res.body.json() as unknown as Subscription;
        if (done(next) || i === tries - 1) {
          setSubscription(next);
          if (done(next)) return true;
        }
      } catch { /* try again */ }
      if (i < tries - 1) await new Promise((r) => setTimeout(r, 1500));
    }
    return false;
  };

  const closeSwitch = () => {
    setSwitchTo(null);
    switchReturnFocus.current?.focus?.();
  };

  const confirmSwitch = async () => {
    if (!switchTo || !switchPreview) return;
    const quote = switchPreview;
    setSwitching(true);
    setSwitchError(null);
    try {
      await apiPost('/subscriptions/switch', { action: 'switch', to: quote.to, quote: quote as unknown as Record<string, unknown> });
      closeSwitch();
      if (quote.to === 'annual') {
        // Charged now: wait for the paid year too, not just the plan, or the card can
        // settle on "annual not started" for a customer who has just paid. Inside a
        // trial there is no annual payment yet, so the plan is enough.
        const shown = await refreshUntil((s) => s.plan === 'annual' && (quote.trialing || s.paidPlan === 'annual'));
        flash(shown ? 'You are now on the annual plan.' : 'Switched to annual. It can take a minute to show here.');
      } else if (quote.scheduled) {
        await refreshUntil((s) => s.pendingPlan === 'monthly');
        flash('Your plan will switch to monthly at the end of your annual year.');
      } else {
        const shown = await refreshUntil((s) => s.plan === 'monthly');
        flash(shown ? 'You are now on the monthly plan.' : 'Switched to monthly. It can take a minute to show here.');
      }
    } catch (err) {
      // The price moved since it was shown (a renewal, a trial ending, the dialog left
      // open): show the new one and ask again, rather than charge what nobody saw.
      const e = await parseApiError(err);
      const fresh = e.body?.quote as SwitchPreview | undefined;
      if (e.code === 'QUOTE_CHANGED' && fresh) {
        setSwitchPreview(fresh);
        setSwitchError('The price changed while this was open. Please check the new one.');
      } else {
        setSwitchError(e.message || 'Your plan could not be changed. Try again.');
      }
    } finally {
      setSwitching(false);
    }
  };

  useEffect(() => {
    if (switchTo) switchFirstButton.current?.focus();
  }, [switchTo]);

  const keepAnnual = async () => {
    setKeepingAnnual(true);
    try {
      await apiPost('/subscriptions/switch', { action: 'keep' });
      await refreshUntil((s) => !s.pendingPlan);
      flash('You are staying on the annual plan.');
    } catch (err) {
      flash((await parseApiError(err)).message || 'Could not keep annual. Try again.', 'error');
    } finally {
      setKeepingAnnual(false);
    }
  };

  // ── Data export ────────────────────────────────────────────────────────────
  const handleExportData = async () => {
    setExportingData(true);
    try {
      const res = await apiPost('/account/export');
      const { url } = await res.body.json() as unknown as { url: string; generatedAt: string; cached: boolean };
      // Trigger the download of the signed ZIP URL
      const a = document.createElement('a');
      a.href = url;
      a.rel = 'noopener';
      a.click();
      flash('Your export is ready — download starting.');
    } catch (e: unknown) {
      const { code } = await parseApiError(e);
      flash(code === 'RATE_LIMITED'
        ? 'You have requested an export too many times recently. Please try again later.'
        : 'Export failed. Please try again.', 'error');
    } finally {
      setExportingData(false);
    }
  };

  // ── Delete account ─────────────────────────────────────────────────────────
  const handleDeleteAccount = async () => {
    try {
      await apiDel('/account');
      signOut();
      navigate('/');
    } catch (err) {
      setShowDeleteConfirm(false);
      // CANCEL_FAILED: the subscription could not be cancelled, so nothing was deleted (#868).
      // DEACTIVATE_FAILED: the account was not deactivated. After either, a subscription the
      // request did cancel stays cancelled, and the server says so (subscriptionCancelled).
      // Trying again is safe. VOID_FAILED: a subscription that had already ended still has
      // an unpaid invoice that could not be closed, which needs support.
      const { code, body } = await parseApiError(err);
      const known = code === 'CANCEL_FAILED' || code === 'DEACTIVATE_FAILED';
      flash(known && body?.subscriptionCancelled === true
        ? 'Your subscription was cancelled, but we could not delete your account. Please try again.'
        : code === 'CANCEL_FAILED'
          ? 'We could not cancel your subscription, so your account was not deleted. Please try again.'
          : code === 'VOID_FAILED'
            ? 'We could not close an unpaid invoice on your account, so your account was not deleted. Please contact support.'
            : code === 'DEACTIVATE_FAILED'
              ? 'We could not delete your account. Please try again.'
              : 'Failed to delete account. Please contact support.', 'error');
    }
  };

  // ── Derived values ─────────────────────────────────────────────────────────
  const avatarSrc = avatarPreview || profile.avatarUrl;
  const subExpiry = subscription?.expiry ?? 0;
  const subPlan = subscription?.plan ?? 'none';
  // The recorded currency, never the visitor's time zone: this is what they pay.
  const subCurrency = recordedCurrency(subscription?.currency);
  const planPriceLabel = (plan: string): string | undefined => (
    plan === 'monthly' ? `${planPrice('monthly', subCurrency)} / month`
      : plan === 'annual' ? `${planPrice('annual', subCurrency)} / year`
        : PLAN_PRICE[plan]
  );
  // Full access for a set time, granted by an admin, with nothing billed (#883). The row
  // can still carry fields from an earlier subscription (cancelAtPeriodEnd, paidPlan,
  // pendingPlan and so on), so every billing value below steps aside for this.
  const isActiveCollaborator = subscription?.plan === 'collaborator' && subscription.active === true;
  // Without an active plan the profile is read-only: the server refuses edits to the
  // photo, bio and location (#1051), so the page never offers them. `active` is computed
  // exactly as the server's check is, and is true for admins. A plan that could not be
  // read locks nothing; the server is the backstop.
  const profileLocked = subscription !== null && subscription.active !== true;

  // The email preference waits for a verified address (#1052). App's claim, unless the
  // server has since said otherwise.
  const addressVerified = emailVerified && !serverSaysUnverified;
  // Verified here: read the profile again, since only now may the token see the
  // address's opt-out, and give focus to the Profile heading, since the form that had it
  // is gone and the section sits at the top of the page.
  const handleVerified = () => {
    setServerSaysUnverified(false);
    onEmailVerified();
    loadProfile();
    profileHeadingRef.current?.focus();
  };
  // The server refused the switch: bring the person to the verification.
  useEffect(() => {
    if (!addressVerified && focusVerify.current) {
      focusVerify.current = false;
      verifyRef.current?.focus();
    }
  }, [addressVerified]);

  // Once locked, show what is saved: an edit or a photo that can no longer be saved must
  // not look saved. And after a refused edit, move focus to the reason, since the button
  // that had it is gone.
  useEffect(() => {
    if (!profileLocked) return;
    setEditProfile({ bio: profile.bio, state: profile.state, country: profile.country, avatarKey: profile.avatarKey });
    setAvatarPreview(null);
  }, [profileLocked, profile]);
  useEffect(() => {
    if (profileLocked && focusLockNote.current) {
      focusLockNote.current = false;
      lockNoteRef.current?.focus();
    }
  }, [profileLocked]);
  // The plan-box branch below also shields a collaborator from the values gated by this
  // (daysLeft, refundWindowEnd, isCancelling, pendingMonthlyAt, the plan badge class and
  // label), so no single test fails without one of them. They stay so that a reorder of
  // the branches never shows a collaborator billing notes.
  const { daysLeft, progress } = (subscription?.active && !subscription.permanent && !isActiveCollaborator && subExpiry > 0)
    ? getTimeRemaining(subExpiry, subPlan, subscription.paidPeriodEnd, subscription.paidPeriodStart)
    : { daysLeft: 0, progress: 0 };
  const renewalDate = subExpiry > 0
    ? new Date(subExpiry).toLocaleDateString('en-US', { year: 'numeric', month: 'long', day: 'numeric' })
    : null;

  // Switched to annual inside a carried trial (#706): chosen, but the first annual
  // payment comes when the trial ends, and until then what was paid for is a month.
  const annualNotStarted = subPlan === 'annual' && subscription?.paidPlan === 'monthly';

  // What was paid for decides a refund, not the plan's current name: an annual
  // subscriber inside a carried trial who switches to monthly still paid for the year.
  const paidAnnual = (subscription?.paidPlan ?? subPlan) === 'annual';

  const refundWindowEnd = (() => {
    if (!subscription?.active || isActiveCollaborator || !paidAnnual) return null;
    // From when the period was paid for, not from when access ends: a carry-over would
    // otherwise stretch a 30-day refund window to nearly 60 (#706). The recorded start
    // is the day of a switch to annual, whose year began earlier (#832); rows from
    // before it fall back to a year back from the end.
    const paidFrom = subscription.paidPeriodStart
      || (subscription.paidPeriodEnd || subExpiry) - 365 * 24 * 60 * 60 * 1000;
    return paidFrom + 30 * 24 * 60 * 60 * 1000;
  })();

  const isRefundEligible = refundWindowEnd !== null && Date.now() <= refundWindowEnd;

  // A paid Stripe subscription: what the plan box below shows as a Monthly or Annual Plan,
  // with its Cancel button. A permanent grant and a trial have none. Deleting the account
  // cancels this one (#868), so only then does the Delete Account card say so.
  const hasPaidSubscription = subscription?.active === true && !subscription.permanent && !subscription.isTrial
    && subscription.plan !== 'collaborator';
  // When the plan box shows Request a refund. The delete confirmation points to it in
  // exactly the same case.
  const showRefundButton = hasPaidSubscription && isRefundEligible;
  // Whether deleting cancels a subscription, for the Delete Account card (#868): a paid one
  // as above, or one whose renewal failed and that Stripe is still trying to collect
  // (past_due). The webhook keeps such a record's status active, so access holds, but stops
  // its expiry at the period paid for, so once that has passed it is no longer active here,
  // though Stripe can still charge it, and deleting cancels it. One set to end at the
  // period end has ended by then. A permanent grant over a subscription the record still
  // holds would need a field the status check does not send, so it is not covered.
  const deleteCancelsSubscription = hasPaidSubscription
    || (subscription?.status === 'active' && subscription.active === false && !subscription.permanent
      && (subscription.plan === 'monthly' || subscription.plan === 'annual') && subscription.cancelAtPeriodEnd !== true);

  const email = user?.signInDetails?.loginId || user?.username || '';
  const initials = getInitials(displayName || email || 'A');

  // ── Plan badge helper ──────────────────────────────────────────────────────
  // Cancelled-but-still-entitled. status stays 'active' until the period actually
  // ends — every access gate reads it — so the pending cancellation is carried by
  // its own flag. These checks used to read status === 'cancelled', which nothing
  // set while the subscription was still active, so the UI below never appeared.
  //
  // Permanent accounts are excluded: an admin grant can land on a row that still
  // carries the flag, and a permanent account has no expiry, so the wind-down note
  // would promise access "until" a blank date. The old status === 'cancelled' half
  // was unreachable once the flag existed, so it is gone.
  const isCancelling = subscription?.cancelAtPeriodEnd === true && subscription?.permanent !== true && !isActiveCollaborator;

  // A switch to monthly scheduled for the end of the paid year, not yet happened. (#832)
  const pendingMonthlyAt = !isActiveCollaborator && subPlan === 'annual' && subscription?.pendingPlan === 'monthly'
    && Number(subscription.pendingPlanAt) > Date.now() && !isCancelling
    ? Number(subscription.pendingPlanAt) : null;

  const planBadgeClass = (() => {
    if (!subscription) return 'inactive';
    if (subscription.permanent) return 'active';
    if (subscription.isTrial) return 'trial';
    if (isActiveCollaborator) return 'active';
    if (isCancelling) return 'cancelling';
    if (subscription.active) return 'active';
    return 'inactive';
  })();

  const planBadgeLabel = (() => {
    if (!subscription) return 'Inactive';
    if (subscription.permanent) return 'Permanent';
    if (subscription.isTrial) return 'Free Trial';
    if (isActiveCollaborator) return 'Collaborator';
    if (isCancelling) return 'Cancelling';
    if (subscription.active) return 'Active';
    return 'Inactive';
  })();

  // ── Rail plan label ────────────────────────────────────────────────────────
  const railPlanLabel = (() => {
    if (!subscription || !subscription.active) return 'No plan';
    if (subscription.permanent) return 'Administrator';
    if (subscription.isTrial) return 'Free Trial';
    if (isActiveCollaborator) return 'Collaborator';
    return subPlan === 'annual' ? 'Annual' : subPlan === 'monthly' ? 'Monthly' : planPriceLabel(subPlan);
  })();

  if (loading) return <div className="loading-container">Loading account details…</div>;

  // ── Render ────────────────────────────────────────────────────────────────

  return (
    <div className="account-shell">

      {/* ── Header ─────────────────────────────────────────────────────────── */}
      <div className="account-head">
        <div className="account-head-inner">
          <h1>My Account</h1>
          <p className="subtitle">Manage your profile and subscription</p>
        </div>
      </div>

      {/* ── Feedback banner ─────────────────────────────────────────────────── */}
      {feedback && (
        <div className="acct-feedback">
          <div className={`acct-feedback-inner ${feedback.type}`}>
            {feedback.type === 'success'
              ? <CheckCircle2 size={17} />
              : <AlertCircle size={17} />}
            {feedback.message}
            <button className="acct-feedback-dismiss" onClick={() => setFeedback(null)}>✕</button>
          </div>
        </div>
      )}

      {/* ── Two-column layout ───────────────────────────────────────────────── */}
      <div className="account-layout">

        {/* ── Left rail ───────────────────────────────────────────────────── */}
        <aside className="account-rail">

          {/* Identity card */}
          <div className="account-id-card">
            <div className="account-id-avatar">
              {avatarSrc
                ? <img src={avatarSrc} alt="Profile" />
                : initials}
            </div>
            <div className="account-id-name">{displayName || 'Your Name'}</div>
            <div className="account-id-email">{email}</div>
            <div className="account-id-plan">{railPlanLabel}</div>
          </div>

          {/* Nav */}
          <nav className="account-nav">
            {NAV_ITEMS.map(({ id, label, icon: Icon, danger }) => (
              <a
                key={id}
                href={`#acct-${id}`}
                className={[
                  activeSection === id ? 'active' : '',
                  danger ? 'danger' : '',
                ].filter(Boolean).join(' ')}
                onClick={(e) => {
                  e.preventDefault();
                  document.getElementById(`acct-${id}`)?.scrollIntoView({ behavior: 'smooth', block: 'start' });
                }}
              >
                <Icon size={16} />
                {label}
              </a>
            ))}
          </nav>
        </aside>

        {/* ── Main content ────────────────────────────────────────────────── */}
        <main className="account-main">

          {/* Verification lives here, not in a site-wide banner (#1130). It is needed to
              subscribe; a reset code reaches any address, so it proves the address works,
              it doesn't unlock recovery. Wording approved by the owner. */}
          {!addressVerified && (
            <div className="acct-note acct-verify" id="acct-verify" ref={verifyRef} role="region" tabIndex={-1} aria-labelledby="acct-verify-text">
              <Info size={15} />
              <div>
                <p id="acct-verify-text">Your email address isn't verified yet. Verify it to subscribe, and to make sure a password reset code can reach you.</p>
                <VerifyEmailInline onVerified={handleVerified} />
              </div>
            </div>
          )}

          {/* ── Profile card ────────────────────────────────────────────── */}
          <section id="acct-profile" className="acct-card">
            <div className="acct-card-head">
              <h2 ref={profileHeadingRef} tabIndex={-1}>Profile</h2>
              <p>How you appear as an author.</p>
            </div>

            <div className="acct-profile">
              {/* Avatar column */}
              <div className="acct-avatar-col">
                {profileLocked ? (
                  <div className="acct-avatar locked">
                    {avatarSrc
                      ? <img src={avatarSrc} alt="Profile" />
                      : initials}
                  </div>
                ) : (
                  <>
                    <div className="acct-avatar" onClick={handleAvatarClick} title="Click to change photo">
                      {avatarSrc
                        ? <img src={avatarSrc} alt="Profile" />
                        : initials}
                      <div className="acct-avatar-overlay"><Camera size={20} /></div>
                    </div>
                    <input
                      ref={fileInputRef}
                      type="file"
                      accept="image/jpeg,image/png,image/webp"
                      style={{ display: 'none' }}
                      onChange={handleAvatarChange}
                    />
                  </>
                )}
              </div>

              {/* Fields */}
              <div className="acct-fields">

                {/* Display name */}
                <div className="acct-field">
                  <label>Display Name</label>
                  {editingName ? (
                    <div className="acct-inline-edit">
                      <input
                        type="text"
                        className="acct-input inline"
                        value={editName}
                        onChange={e => setEditName(e.target.value)}
                        autoFocus
                      />
                      <button className="btn-primary btn-sm" onClick={handleSaveName} disabled={savingName || !editName.trim()}>
                        {savingName ? 'Saving…' : 'Save'}
                      </button>
                      <button className="btn-secondary btn-sm" onClick={() => { setEditingName(false); setEditName(displayName); }}>
                        Cancel
                      </button>
                    </div>
                  ) : (
                    <div className="acct-field-value">
                      <span>{displayName || <em style={{ color: 'var(--color-text-muted)' }}>Not set</em>}</span>
                      <button className="acct-icon-btn" onClick={() => setEditingName(true)} title="Edit display name">
                        <Pencil size={14} />
                      </button>
                    </div>
                  )}
                </div>

                {/* Email */}
                <div className="acct-field">
                  <label>Email</label>
                  <div className="acct-field-value">{email}</div>
                </div>

                {/* Bio */}
                <div className="acct-field">
                  <label htmlFor="acct-bio">About the Author</label>
                  <textarea
                    id="acct-bio"
                    aria-describedby={profileLocked ? 'acct-profile-locked' : undefined}
                    className="acct-textarea"
                    rows={4}
                    placeholder={profileLocked ? undefined : 'Tell readers a bit about yourself…'}
                    value={editProfile.bio}
                    onChange={e => setEditProfile(p => ({ ...p, bio: e.target.value }))}
                    maxLength={1000}
                    readOnly={profileLocked}
                  />
                  {!profileLocked && <span className="acct-char-count">{editProfile.bio.length} / 1000</span>}
                </div>

                {/* State / Country */}
                <div className="acct-field">
                  <div className="acct-loc-row">
                    <div>
                      <label htmlFor="acct-state">State / Region</label>
                      <input
                        id="acct-state"
                        aria-describedby={profileLocked ? 'acct-profile-locked' : undefined}
                        type="text"
                        className="acct-input"
                        placeholder={profileLocked ? undefined : 'e.g. California'}
                        value={editProfile.state}
                        onChange={e => setEditProfile(p => ({ ...p, state: e.target.value }))}
                        maxLength={100}
                        readOnly={profileLocked}
                      />
                    </div>
                    <div>
                      <label htmlFor="acct-country">Country</label>
                      <input
                        id="acct-country"
                        aria-describedby={profileLocked ? 'acct-profile-locked' : undefined}
                        type="text"
                        className="acct-input"
                        placeholder={profileLocked ? undefined : 'e.g. United States'}
                        value={editProfile.country}
                        onChange={e => setEditProfile(p => ({ ...p, country: e.target.value }))}
                        maxLength={100}
                        readOnly={profileLocked}
                      />
                    </div>
                  </div>
                </div>

                {/* Save, or why there is none (wording approved by the owner, #1051) */}
                {profileLocked ? (
                  <div className="acct-note" ref={lockNoteRef} tabIndex={-1}>
                    <Info size={15} />
                    <p id="acct-profile-locked">
                      Choose a plan in{' '}
                      <a
                        href="#acct-plan"
                        onClick={(e) => {
                          e.preventDefault();
                          document.getElementById('acct-plan')?.scrollIntoView({ behavior: 'smooth', block: 'start' });
                        }}
                      >Plan &amp; Billing</a>
                      {' '}to edit your profile.
                    </p>
                  </div>
                ) : (
                  <div className="acct-save-row">
                    <button className="btn-primary" onClick={handleSaveProfile} disabled={savingProfile || uploadingAvatar}>
                      {savingProfile ? 'Saving…' : 'Save profile'}
                    </button>
                  </div>
                )}

              </div>
            </div>
          </section>

          {/* ── Plan & Billing card ──────────────────────────────────────── */}
          <section id="acct-plan" className="acct-card">
            <div className="acct-card-head">
              <h2>Plan &amp; Billing</h2>
            </div>

            {subscription?.permanent ? (
              /* Administrator / permanent */
              <div className="plan-box">
                <div className="plan-top">
                  <div>
                    <div className="plan-name">Administrator</div>
                    <div className="plan-price">Permanent access</div>
                  </div>
                  <span className="plan-badge active">Permanent</span>
                </div>
              </div>

            ) : subscription?.isTrial ? (
              /* Free trial */
              <div className="plan-box">
                <div className="plan-top">
                  <div>
                    <div className="plan-name">Free Trial</div>
                    <div className="plan-price">1-book limit &middot; full features</div>
                  </div>
                  <span className="plan-badge trial">Free Trial</span>
                </div>
                {subscription.trialDaysRemaining != null && (
                  <div className="plan-timeline">
                    <div className="plan-bar">
                      <div className="plan-bar-fill" style={{ width: `${Math.max(0, 100 - (subscription.trialDaysRemaining / 30) * 100)}%` }} />
                    </div>
                    <div className="plan-timeline-labels">
                      <span className={`plan-days ${subscription.trialDaysRemaining <= 5 ? 'urgent' : ''}`}>
                        {subscription.trialDaysRemaining} day{subscription.trialDaysRemaining !== 1 ? 's' : ''} remaining
                      </span>
                      <span className="plan-renew">Trial ends {renewalDate}</span>
                    </div>
                  </div>
                )}
                <div className="plan-divider-note">
                  <Info size={15} />
                  Subscribe before your trial ends to keep access to all your books and cloud features.
                </div>
                <div className="plan-actions">
                  <button className="btn-primary" onClick={() => navigate('/pricing')}>
                    View subscription plans
                  </button>
                </div>
              </div>

            ) : isActiveCollaborator && subscription ? (
              /* Collaborator: full access for a set time, granted by an admin (#883) */
              <div className="plan-box">
                <div className="plan-top">
                  <div>
                    <div className="plan-name">Collaborator</div>
                    <div className="plan-price">Full access until {formatUntil(subscription.expiry)}</div>
                  </div>
                  <span className="plan-badge active">Collaborator</span>
                </div>
              </div>

            ) : subscription?.active ? (
              /* Active (including cancelling) */
              <div className="plan-box">
                <div className="plan-top">
                  <div>
                    <div className="plan-name">{subPlan === 'annual' ? 'Annual Plan' : 'Monthly Plan'}</div>
                    <div className="plan-price">{planPriceLabel(subPlan)}</div>
                  </div>
                  <span className={`plan-badge ${planBadgeClass}`}>{planBadgeLabel}</span>
                </div>

                {subExpiry > 0 && (
                  <div className="plan-timeline">
                    <div className="plan-bar">
                      <div className="plan-bar-fill" style={{ width: `${progress}%` }} />
                    </div>
                    <div className="plan-timeline-labels">
                      <span className={`plan-days ${daysLeft <= 5 ? 'urgent' : ''}`}>
                        {daysLeft} day{daysLeft !== 1 ? 's' : ''} remaining
                      </span>
                      <span className="plan-renew">
                        {isCancelling ? 'Access ends'
                          : pendingMonthlyAt ? 'Monthly from'
                            : annualNotStarted ? 'Annual billing starts' : 'Renews'} {renewalDate}
                      </span>
                    </div>
                  </div>
                )}

                {isCancelling && (
                  <div className="plan-divider-note">
                    <Info size={15} />
                    You keep full access — including the desktop app — until <strong>{renewalDate}</strong>.
                  </div>
                )}

                {pendingMonthlyAt && (
                  <div className="plan-divider-note">
                    <Info size={15} />
                    <span>
                      Your annual plan continues until <strong>{longDate(pendingMonthlyAt)}</strong>, then
                      switches to monthly at {planPriceLabel('monthly')}.
                    </span>
                  </div>
                )}

                {annualNotStarted && renewalDate && (
                  <div className="plan-divider-note">
                    <Info size={15} />
                    <span>
                      Your annual plan starts on <strong>{renewalDate}</strong>, when {planPrice('annual', subCurrency)} is
                      charged. The month you have paid for runs until then.
                    </span>
                  </div>
                )}

                {refundWindowEnd !== null && (
                  <div style={{ fontSize: 12, color: isRefundEligible ? '#0abfbc' : '#aaa', marginBottom: 8 }}>
                    {isRefundEligible
                      ? `Eligible for a full refund until ${new Date(refundWindowEnd).toLocaleDateString('en-US', { month: 'long', day: 'numeric', year: 'numeric' })}`
                      : `Refund window closed ${new Date(refundWindowEnd).toLocaleDateString('en-US', { month: 'long', day: 'numeric', year: 'numeric' })}`}
                  </div>
                )}
                <div className="plan-actions">
                  {/* Switching plan mid-wind-down is ambiguous, so it steps aside while a
                      cancellation is pending. Everything else stays put: billing access and
                      an earned refund must not vanish just because the plan is ending. */}
                  {/* The switch happens here, not in Stripe's portal, which cannot schedule
                      annual to monthly between our two products. (#832) */}
                  {!isCancelling && (pendingMonthlyAt ? (
                    <button className="btn-secondary btn-sm" onClick={keepAnnual} disabled={keepingAnnual}>
                      {keepingAnnual ? 'Keeping annual…' : 'Keep annual'}
                    </button>
                  ) : (
                    <button className="btn-secondary btn-sm" onClick={() => openSwitch(subPlan === 'monthly' ? 'annual' : 'monthly')}>
                      {subPlan === 'monthly' ? 'Switch to Annual (save 17%)' : 'Switch to Monthly'}
                    </button>
                  ))}
                  <button className="btn-secondary btn-sm" onClick={handleManageBilling} disabled={managingBilling}>
                    <Receipt size={14} /> {managingBilling ? 'Opening…' : 'Manage billing'}
                  </button>
                  {isCancelling ? (
                    // The Stripe billing portal is the only thing that can actually undo a
                    // pending cancellation. This pointed at /pricing, which would have sold
                    // a second subscription to someone who already holds a live one.
                    <button className="btn-primary" onClick={handleManageBilling} disabled={managingBilling}>
                      <RefreshCw size={15} /> {managingBilling ? 'Opening…' : 'Resume subscription'}
                    </button>
                  ) : (
                    <button className="btn-danger-ghost" onClick={() => setShowCancelModal(true)}>
                      Cancel
                    </button>
                  )}
                  {/* Cancelling and then asking for the money back is the normal order, so
                      the refund has to outlive the cancellation. */}
                  {showRefundButton && (
                    <button className="btn-danger-ghost" onClick={() => setShowRefundModal(true)}>
                      Request a refund
                    </button>
                  )}
                </div>
              </div>

            ) : (
              /* No subscription */
              <div className="plan-box">
                <div className="plan-top">
                  <div>
                    <div className="plan-name">No active subscription</div>
                    <div className="plan-price">Subscribe to unlock cloud storage and the desktop app.</div>
                  </div>
                  <span className="plan-badge inactive">Inactive</span>
                </div>
                <div className="plan-actions">
                  <button className="btn-primary" onClick={() => navigate('/pricing')}>View plans</button>
                </div>
              </div>
            )}
          </section>

          {/* ── Security card ────────────────────────────────────────────── */}
          <section id="acct-security" className="acct-card">
            <div className="acct-card-head">
              <h2>Security</h2>
            </div>

            {/* Recovery key. Shown to email accounts, and to any account the server says
                has a key. Offering Create to Google and Apple accounts is not done: the
                owner's decision (#1105). But a Google or Apple sign-in linked to an email
                account has a password, and likely a key from the old sign-up gate, so one
                that already has a key still sees it and can replace it: hiding the row
                would hide a live credential. A Google or Apple account with no key, or
                whose status is still loading or could not be read, has no row. Optional
                since #1105: made here behind an emailed code, never at sign-up. */}
            {(recoveryKeyStatus === 'set' || !isFederated) && (
              <div className="acct-row">
                <div>
                  <div className="acct-row-label">Recovery key</div>
                  <div className="acct-row-sub">Lets you reset your password if you ever lose access to your email</div>
                </div>
                {/* Empty while loading, so it never shows "Not set up" before the answer.
                    The dialog keeps its place from 'none' to 'set', so a flow that is open
                    as the status is read again stays open. */}
                <div className="acct-row-right acct-row-right-stack">
                  {recoveryKeyStatus === 'set' && (
                    <span className="acct-status-ok">
                      <CheckCircle2 size={15} /> {recoveryKeyDate ? `Set ${recoveryKeyDate}` : 'Set'}
                    </span>
                  )}
                  {recoveryKeyStatus === 'none' && (
                    <span className="acct-status-warn"><AlertCircle size={15} /> Not set up</span>
                  )}
                  {recoveryKeyStatus === 'unknown' && (
                    <span className="acct-status-warn"><AlertCircle size={15} /> Couldn't check right now</span>
                  )}
                  {(recoveryKeyStatus === 'set' || recoveryKeyStatus === 'none') && (
                    <RegenerateRecoveryKey
                      mode={recoveryKeyStatus === 'set' ? 'replace' : 'create'}
                      email={tokenEmail}
                      onReplaced={fetchRecoveryKeyStatus}
                    />
                  )}
                </div>
              </div>
            )}

            {/* Password */}
            <div className="acct-row">
              <div>
                <div className="acct-row-label">Password</div>
                <div className="acct-row-sub">
                  {isFederated
                    ? 'You sign in with Google or Apple — manage your password with that provider.'
                    : 'Change your sign-in password'}
                </div>
              </div>
              <div className="acct-row-right">
                {!isFederated && (
                <button
                  className="btn-secondary btn-sm"
                  onClick={() => { setPasswordError(null); setShowPasswordModal(true); }}
                >
                  Change
                </button>
                )}
              </div>
            </div>

            {/* 2FA - Future feature 
            <div className="acct-row">
              <div>
                <div className="acct-row-label">Two-step verification</div>
                <div className="acct-row-sub">Add an extra layer of security at sign-in</div>
              </div>
              <div className="acct-row-right">
                <span className="acct-status-warn"><AlertCircle size={15} /> Not set up</span>
              </div>
            </div>
            */}
          </section>

          {/* ── Your Data card ───────────────────────────────────────────── */}
          <section id="acct-data" className="acct-card">
            <div className="acct-card-head">
              <h2>Your Data</h2>
            </div>

            {/* Export */}
            <div className="acct-row">
              <div>
                <div className="acct-row-label">Export everything</div>
                <div className="acct-row-sub">Download a ZIP of all your manuscripts (.chaptery files), world building, and storyboards</div>
              </div>
              <div className="acct-row-right">
                <button className="btn-secondary btn-sm" onClick={handleExportData} disabled={exportingData}>
                  <Download size={14} /> {exportingData ? 'Preparing…' : 'Export'}
                </button>
              </div>
            </div>

            {/* Privacy & Terms */}
            <div className="acct-row">
              <div>
                <div className="acct-row-label">Privacy &amp; terms</div>
              </div>
              <div className="acct-row-right">
                <a href="/privacy" target="_blank" rel="noopener noreferrer" className="acct-link">Privacy Policy</a>
                <a href="/tos" target="_blank" rel="noopener noreferrer" className="acct-link">Terms</a>
              </div>
            </div>
          </section>

          {/* ── Email preferences card ───────────────────────────────────── */}
          <section id="acct-emails" className="acct-card">
            <div className="acct-card-head">
              <h2>Email Preferences</h2>
              <p>Choose whether we email you when a new version of Chaptery is released.</p>
            </div>

            <div className="acct-row">
              <div>
                <div className="acct-row-label">Product update emails</div>
                <div className="acct-row-sub">
                  A short note when a new version ships, with what changed and a link to update.
                  Roughly once a month, never more than one email per release.
                </div>
              </div>
              {addressVerified && (
                <div className="acct-row-right">
                  <button
                    type="button"
                    role="switch"
                    aria-checked={profile.emailUpdates}
                    aria-label="Product update emails"
                    className={`acct-switch ${profile.emailUpdates ? 'on' : ''}`}
                    disabled={savingEmailPref}
                    onClick={() => setEmailUpdates(!profile.emailUpdates)}
                  >
                    <span className="acct-switch-knob" />
                  </button>
                  <span className="acct-switch-state">{profile.emailUpdates ? 'On' : 'Off'}</span>
                </div>
              )}
            </div>

            {/* Update emails go only to verified addresses, and the preference acts on an
                address that may be someone else's until it is proved, so the server refuses
                it (#1052). No switch that does nothing: say so, and point to the verification
                at the top of the page. Wording approved by the owner. */}
            {!addressVerified && (
              <div className="acct-note">
                <Info size={15} />
                <div>
                  <p>
                    Update emails go only to verified addresses.{' '}
                    <a
                      href="#acct-verify"
                      onClick={(e) => {
                        e.preventDefault();
                        verifyRef.current?.focus();
                      }}
                    >Verify yours</a>
                    {' '}to get them.
                  </p>
                </div>
              </div>
            )}

            {emailPrefError && (
              <div className="acct-inline-error" role="alert">
                <AlertCircle size={15} /> {emailPrefError}
              </div>
            )}

            {/* Turning the switch off must not imply silence. These are operational
                and legal notices, and a reader who believes they have opted out of
                everything is the person most likely to miss a deletion warning. */}
            <div className="acct-note">
              <Info size={15} />
              <div>
                {/* No reference to the switch: an unverified account has none (#1052).
                    Wording approved by the owner. */}
                <strong>You will still get essential emails</strong> about your account: billing and
                receipts, subscription changes, refunds, password and security notices, and warnings
                before an account is deleted.
              </div>
            </div>
          </section>

          {/* ── Support card ─────────────────────────────────────────────── */}
          <section id="acct-support" className="acct-card">
            <div className="acct-card-head">
              <h2>Support</h2>
              <p>Can't find what you need? Email <a href="mailto:support@trychaptery.com" className="acct-link">support@trychaptery.com</a> — we respond within 1–2 business days.</p>
            </div>

            {isHelpAdmin && (
              <div className="acct-row" style={{ marginBottom: 16 }}>
                <div>
                  <div className="acct-row-label">Help Article Admin</div>
                  <div className="acct-row-sub">Edit help center articles</div>
                </div>
                <div className="acct-row-right">
                  <a href="/admin/help" className="btn-secondary btn-sm" style={{ textDecoration: 'none' }}>
                    Open editor →
                  </a>
                </div>
              </div>
            )}

            <div className="acct-faq">
              {/* How do I cancel? */}
              <div className={`acct-faq-item ${supportOpen === 'cancel' ? 'open' : ''}`}>
                <button className="acct-faq-q" onClick={() => toggleSupport('cancel')}>
                  How do I cancel?
                  <ChevronDown size={18} className="chev" />
                </button>
                <div className="acct-faq-a">
                  <div className="acct-faq-a-inner">
                    You can cancel at any time — no waiting periods, no phone calls.<br /><br />
                    <strong>How to cancel:</strong> Go to <strong>Plan &amp; Billing</strong> above and click <strong>Cancel</strong>. Optionally tell us why, then confirm.<br /><br />
                    <strong>What happens next:</strong> Your subscription won't renew at the end of the current billing period. You keep full access right up until then.<br /><br />
                    <strong>Changed your mind?</strong> Click <strong>Resume subscription</strong> in Plan &amp; Billing before your period ends and nothing changes — same plan, same price, no new purchase.
                  </div>
                </div>
              </div>

              {/* Refunds */}
              <div className={`acct-faq-item ${supportOpen === 'refunds' ? 'open' : ''}`}>
                <button className="acct-faq-q" onClick={() => toggleSupport('refunds')}>
                  What is your refund policy?
                  <ChevronDown size={18} className="chev" />
                </button>
                <div className="acct-faq-a">
                  <div className="acct-faq-a-inner">
                    <strong>Annual subscription</strong> — full refund within 30 days of purchase or most recent renewal. Switching from monthly to annual counts as a purchase: the 30 days run from when you are charged for the annual plan, and a refund returns that charge along with the unused part of your last month.<br />
                    <strong>Monthly subscription</strong> — not eligible for refunds. Cancel before your next renewal date and you won't be charged again.<br /><br />
                    <strong>To get a refund:</strong> Click <strong>Request a refund</strong> in <strong>Plan &amp; Billing</strong> above. The button is there while your refund window is open. Unlike cancelling, a refund ends your access straight away. The money goes back to your original payment method and usually appears within 5–10 business days. If something goes wrong, email <a href="mailto:support@trychaptery.com">support@trychaptery.com</a> from the address on your account.
                  </div>
                </div>
              </div>

              {/* Data */}
              <div className={`acct-faq-item ${supportOpen === 'data' ? 'open' : ''}`}>
                <button className="acct-faq-q" onClick={() => toggleSupport('data')}>
                  What happens to my books if I cancel?
                  <ChevronDown size={18} className="chev" />
                </button>
                <div className="acct-faq-a">
                  <div className="acct-faq-a-inner">
                    <strong>After cancellation:</strong> Your data stays in your account until the billing period ends. After that you can still log in to export it.<br /><br />
                    <strong>After a refund:</strong> Access ends immediately. Your data is retained for 30 days before permanent deletion — email us to request an export during that window.<br /><br />
                    <strong>Export at any time:</strong> Go to <strong>Your Data</strong> above and click <strong>Export</strong>.
                  </div>
                </div>
              </div>

              {/* Contact */}
              <div className={`acct-faq-item ${supportOpen === 'contact' ? 'open' : ''}`}>
                <button className="acct-faq-q" onClick={() => toggleSupport('contact')}>
                  I still need help
                  <ChevronDown size={18} className="chev" />
                </button>
                <div className="acct-faq-a">
                  <div className="acct-faq-a-inner">
                    We're a small team and we genuinely want to help.<br /><br />
                    <strong>Email:</strong> <a href="mailto:support@trychaptery.com">support@trychaptery.com</a><br />
                    <strong>Response time:</strong> 1–2 business days<br /><br />
                    For billing questions, disputes, or anything you'd rather not handle through automated flows, email is always the right place to start.
                  </div>
                </div>
              </div>
            </div>
          </section>

          {/* ── Delete Account card ──────────────────────────────────────── */}
          <section id="acct-danger" className="acct-card danger">
            <div className="acct-card-head">
              <h2>Delete Account</h2>
            </div>

            {!showDeleteConfirm ? (
              <div className="acct-danger-body">
                <p>
                  {deleteCancelsSubscription
                    ? 'Deleting your account cancels your subscription right away, with no refund for the rest of the current period. It also permanently deletes your account, all books, world data, and subscription records.'
                    : 'Permanently deletes your account, all books, world data, and subscription records.'}
                  {' '}You can ask us to restore it within 30 days; after that it cannot be undone.
                </p>
                <button className="btn-danger-ghost" onClick={() => setShowDeleteConfirm(true)}>
                  <Trash2 size={15} /> Delete account
                </button>
              </div>
            ) : (
              <div className="acct-delete-confirm">
                {deleteCancelsSubscription ? (
                  <p><strong>Are you absolutely sure?</strong> Deleting your account cancels your subscription right away. You will not be charged again, and there is no refund for the rest of the current period. Your account will be deactivated immediately and permanently deleted, along with all your books, world building, and revisions, after 30 days. You can email support@trychaptery.com to restore it any time within that window, but restoring it does not restart your subscription; after 30 days it cannot be recovered.</p>
                ) : (
                  <p><strong>Are you absolutely sure?</strong> Your account will be deactivated immediately and permanently deleted, along with all your books, world building, and revisions, after 30 days. You can email support@trychaptery.com to restore it any time within that window; after 30 days it cannot be recovered.</p>
                )}
                {/* Exactly when the Request a refund button above is shown (#868). */}
                {showRefundButton && (
                  <p>You can still request a refund for your annual plan. Do that before deleting, because deleting cancels without a refund.</p>
                )}
                <div className="acct-delete-actions">
                  <button className="btn-danger-solid" onClick={handleDeleteAccount}>
                    Yes, delete my account
                  </button>
                  <button className="btn-secondary btn-sm" onClick={() => setShowDeleteConfirm(false)}>
                    Keep my account
                  </button>
                </div>
              </div>
            )}
          </section>

        </main>
      </div>

      {/* ── Cancel subscription modal ────────────────────────────────────────── */}
      {showCancelModal && (
        <div className="acct-modal-overlay" onClick={() => setShowCancelModal(false)}>
          <div className="acct-modal" onClick={e => e.stopPropagation()}>
            <div className="acct-modal-head">
              <h3>Cancel Subscription</h3>
              <button className="acct-icon-btn" onClick={() => setShowCancelModal(false)}><X size={18} /></button>
            </div>

            <div className="acct-modal-keepnote">
              <strong>Your account stays active.</strong> Cancelling only stops future renewal — you keep full access including the desktop app until <strong>{renewalDate}</strong>.
            </div>

            {pendingMonthlyAt && (
              <p style={{ marginTop: '1rem', fontSize: '0.9rem', color: 'var(--color-text-secondary)' }}>
                Cancelling also removes your switch to monthly. If you resume later, your plan renews as annual.
              </p>
            )}

            <div style={{ marginTop: '1rem' }}>
              <label style={{ display: 'block', fontSize: '0.82rem', fontWeight: 600, marginBottom: '6px', color: 'var(--color-text-muted)', textTransform: 'uppercase', letterSpacing: '0.03em' }}>
                What's your reason? <span style={{ fontWeight: 400 }}>(optional)</span>
              </label>
              <select className="acct-select" value={cancelReason} onChange={e => setCancelReason(e.target.value)}>
                <option value="">Select a reason…</option>
                <option value="too_expensive">Too expensive</option>
                <option value="not_using">Not using it enough</option>
                <option value="missing_features">Missing features I need</option>
                <option value="switching">Switching to another tool</option>
                <option value="temporary">Taking a break temporarily</option>
                <option value="other">Other</option>
              </select>
            </div>

            <div className="acct-modal-actions">
              <button className="btn-secondary" onClick={() => setShowCancelModal(false)} disabled={cancelling}>
                Keep my subscription
              </button>
              <button className="btn-danger-solid" onClick={handleCancelSubscription} disabled={cancelling}>
                {cancelling ? 'Cancelling…' : 'Yes, cancel'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* ── Plan switch modal (#832) ─────────────────────────────────────────── */}
      {switchTo && (() => {
        const q = switchPreview;
        const qCurrency = recordedCurrency(q?.currency);
        const charge = q ? formatMoney(q.chargeNow / 100, qCurrency) : '';
        const confirmLabel = switching ? 'Switching…'
          : !q ? 'Switch'
            : q.to === 'annual' && q.chargeNow > 0 ? `Switch and pay ${charge}`
              : q.to === 'annual' ? 'Switch to annual'
                : q.scheduled ? 'Switch at the end of my year' : 'Switch to monthly';
        return (
          <div className="acct-modal-overlay" onClick={() => !switching && closeSwitch()}>
            <div
              className="acct-modal" role="dialog" aria-modal="true" aria-labelledby="switch-title"
              onClick={e => e.stopPropagation()}
              onKeyDown={e => {
                if (e.key === 'Escape' && !switching) closeSwitch();
                if (e.key === 'Tab') {
                  const focusable = Array.from(e.currentTarget.querySelectorAll<HTMLElement>('button:not([disabled])'));
                  const first = focusable[0];
                  const last = focusable[focusable.length - 1];
                  if (e.shiftKey && document.activeElement === first) { e.preventDefault(); last?.focus(); }
                  else if (!e.shiftKey && document.activeElement === last) { e.preventDefault(); first?.focus(); }
                }
              }}
            >
              <div className="acct-modal-head">
                <h3 id="switch-title">{switchTo === 'annual' ? 'Switch to annual' : 'Switch to monthly'}</h3>
                <button className="acct-icon-btn" aria-label="Close" onClick={closeSwitch} disabled={switching}><X size={18} /></button>
              </div>

              <div className="acct-modal-keepnote" data-testid="switch-terms" aria-live="polite">
                {!q && !switchError && 'Working out the price…'}
                {q?.to === 'annual' && q.chargeNow === 0 && q.trialing && (
                  <>
                    Nothing is charged today. From <strong>{longDate(q.at ?? Date.now())}</strong> you&rsquo;ll pay
                    {' '}{planPrice('annual', qCurrency)} a year. Change your mind within 30 days of that first annual payment and
                    you get it all back.
                  </>
                )}
                {q?.to === 'annual' && q.chargeNow > 0 && (
                  <>
                    You&rsquo;ll be charged <strong>{charge}</strong> today: the annual price for the rest of this billing year,
                    less the unused part of your current month.
                    {q.yearEndsAt ? <> Your annual plan runs until <strong>{longDate(q.yearEndsAt)}</strong>.</> : null}
                    {' '}Change your mind within 30 days and you get it all back, including the unused part of this month.
                  </>
                )}
                {q?.to === 'annual' && !q.trialing && q.chargeNow === 0 && (
                  <>
                    Nothing is charged today: your account credit covers it.
                    {q.yearEndsAt ? <> Your annual plan runs until <strong>{longDate(q.yearEndsAt)}</strong>.</> : null}
                  </>
                )}
                {q?.to === 'monthly' && q.scheduled && q.at && (
                  <>
                    Your annual plan continues until <strong>{longDate(q.at)}</strong>. From then you&rsquo;ll pay
                    {' '}{planPrice('monthly', qCurrency)} a month. Nothing is charged today, and you can undo this any time
                    before {longDate(q.at)}.
                  </>
                )}
                {q?.to === 'monthly' && !q.scheduled && q.at && (
                  annualNotStarted ? (
                    <>
                      Your plan goes back to monthly: {planPrice('monthly', qCurrency)} a month from <strong>{longDate(q.at)}</strong>.
                      Nothing is charged today.
                    </>
                  ) : (
                    <>
                      What you have now continues until <strong>{longDate(q.at)}</strong>. From then you&rsquo;ll pay
                      {' '}{planPrice('monthly', qCurrency)} a month. Nothing is charged today.
                    </>
                  )
                )}
              </div>

              {switchError && <div className="feedback-banner feedback-error" role="alert" style={{ marginTop: '1rem' }}>{switchError}</div>}

              <div className="acct-modal-actions">
                <button ref={switchFirstButton} className="btn-secondary" onClick={closeSwitch} disabled={switching}>
                  Not now
                </button>
                <button className="btn-primary" onClick={confirmSwitch} disabled={switching || !q}>
                  {confirmLabel}
                </button>
              </div>
            </div>
          </div>
        );
      })()}

      {/* ── Change password modal ───────────────────────────────────────────── */}
      {showPasswordModal && (
        <div className="acct-modal-overlay" onClick={closePasswordModal}>
          <div className="acct-modal" onClick={e => e.stopPropagation()}>
            <div className="acct-modal-head">
              <h3>Change Password</h3>
              <button className="acct-icon-btn" onClick={closePasswordModal}><X size={18} /></button>
            </div>

            <form onSubmit={e => { e.preventDefault(); handleChangePassword(); }}>
              <div style={{ marginTop: '0.5rem' }}>
                <label style={{ display: 'block', fontSize: '0.82rem', fontWeight: 600, marginBottom: '6px', color: 'var(--color-text-muted)', textTransform: 'uppercase', letterSpacing: '0.03em' }}>
                  Current password
                </label>
                <input className="acct-input" type="password" autoComplete="current-password" value={currentPassword} onChange={e => setCurrentPassword(e.target.value)} />
              </div>
              <div style={{ marginTop: '0.85rem' }}>
                <label style={{ display: 'block', fontSize: '0.82rem', fontWeight: 600, marginBottom: '6px', color: 'var(--color-text-muted)', textTransform: 'uppercase', letterSpacing: '0.03em' }}>
                  New password
                </label>
                <input className="acct-input" type="password" autoComplete="new-password" value={newPassword} onChange={e => setNewPassword(e.target.value)} />
                <div style={{ fontSize: '0.78rem', color: 'var(--color-text-muted)', marginTop: '4px' }}>
                  At least 8 characters, with an uppercase letter, a lowercase letter, and a number.
                </div>
              </div>
              <div style={{ marginTop: '0.85rem' }}>
                <label style={{ display: 'block', fontSize: '0.82rem', fontWeight: 600, marginBottom: '6px', color: 'var(--color-text-muted)', textTransform: 'uppercase', letterSpacing: '0.03em' }}>
                  Confirm new password
                </label>
                <input className="acct-input" type="password" autoComplete="new-password" value={confirmPassword} onChange={e => setConfirmPassword(e.target.value)} />
              </div>

              {passwordError && (
                <div className="acct-status-warn" style={{ marginTop: '0.85rem', fontSize: '0.85rem' }}>
                  <AlertCircle size={15} /> {passwordError}
                </div>
              )}

              <div className="acct-modal-actions">
                <button type="button" className="btn-secondary" onClick={closePasswordModal} disabled={changingPassword}>
                  Cancel
                </button>
                <button type="submit" className="btn-primary" disabled={changingPassword}>
                  {changingPassword ? 'Updating…' : 'Update password'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* ── Refund modal ────────────────────────────────────────────────────── */}
      {showRefundModal && (
        <div className="acct-modal-overlay" onClick={() => { setShowRefundModal(false); setRefundReason(''); }}>
          <div className="acct-modal" onClick={e => e.stopPropagation()}>
            <div className="acct-modal-head">
              <h3>Request a Refund</h3>
              <button className="acct-icon-btn" onClick={() => { setShowRefundModal(false); setRefundReason(''); }}><X size={18} /></button>
            </div>

            <div className="acct-modal-keepnote">
              <strong>Your access ends immediately.</strong> Unlike cancellation, a refund deactivates your account right away. You'll have 30 days to export your data.
            </div>

            <p style={{ marginTop: '1rem', fontSize: '0.9rem', color: 'var(--color-text-secondary)' }}>
              Once processed, the refund typically appears on your statement within 5–10 business days.
            </p>

            <div style={{ marginTop: '0.75rem' }}>
              <label style={{ fontSize: '0.85rem', color: 'var(--color-text-secondary)', display: 'block', marginBottom: 6 }}>
                Reason (optional)
              </label>
              <textarea
                className="admin-field admin-textarea"
                rows={3}
                placeholder="Help us improve — what prompted this refund?"
                value={refundReason}
                onChange={e => setRefundReason(e.target.value)}
                disabled={refunding}
              />
            </div>

            <div className="acct-modal-actions">
              <button className="btn-secondary" onClick={() => { setShowRefundModal(false); setRefundReason(''); }} disabled={refunding}>
                Keep my subscription
              </button>
              <button className="btn-danger-solid" onClick={handleRefundRequest} disabled={refunding}>
                {refunding ? 'Processing…' : 'Yes, refund my payment'}
              </button>
            </div>
          </div>
        </div>
      )}

    </div>
  );
};

export default AccountPage;
